import { getLocalizeKeyNames, I18n, LABEL_CATEGORY } from '../hooks/I18n';
import { defined, mergeRecords, Optional, returnIf } from '../utils/Util';
import { VALUE_UNDEFINED } from './DataUtil';
import { RaceData } from './RaceDataUtil';

const VALUE_UNDEFINED_JP = '(未計算)';

export interface ValueFormatter {
  format: (value: string, i18n: I18n, raceData: RaceData) => string;
  kana: (value: string, i18n: I18n) => { kana?: string };
  asNumber: (value: string) => number;
  supportsKana: () => boolean;
}

class FormatError extends Error {}

const check: (value: boolean, message?: string) => asserts value is true = (
  value: boolean,
  message = ''
): void => {
  if (!value) {
    throw new FormatError(message);
  }
};

export const getFormatter = (id: string): Optional<ValueFormatter> =>
  valueFormatterDict[id];

export const formatOrLocalize = (
  key: string,
  value: string,
  i18n: I18n,
  raceData: RaceData
): { displayName: string; kana: string } => {
  const formatter = getFormatter(key);
  return {
    displayName: formatter?.format(value, i18n, raceData) ?? value,
    kana: formatter?.kana(value, i18n).kana ?? value,
  };
};

const createDefaultFormatter = (
  format: (value: string, i18n: I18n, raceData: RaceData) => string,
  {
    kana,
    supportsKana,
    asNumber,
  }: {
    kana?: (value: string, i18n: I18n) => { kana?: string };
    supportsKana?: () => boolean;
    asNumber?: (value: string) => number;
  } = {}
): ValueFormatter => {
  kana = kana ?? (() => ({}));
  supportsKana = supportsKana ?? (() => false);
  asNumber = asNumber ?? ((value) => Number(value));

  return {
    format,
    kana,
    supportsKana,
    asNumber,
  };
};

const timesFormatter: ValueFormatter = createDefaultFormatter((value) => {
  if (value === VALUE_UNDEFINED) {
    // 達成記録のfastest/ppでは達成までのレース数でUNKNOWNの記録がUNDEFINEDになる
    return VALUE_UNDEFINED_JP;
  }
  check(Number.isInteger(Number(value)));
  return value + '回';
});

const contTimesFormatter: ValueFormatter = createDefaultFormatter((value) => {
  const num = Number(value);
  check(Number.isInteger(num));
  const postfix = num === 1 ? '回' : '回連続';
  return `${String(num)}${postfix}`;
});

const rankFormatter: ValueFormatter = createDefaultFormatter((value) => {
  const num = Number(value);
  check(Number.isInteger(num));
  // レース結果順位はリタイアの場合-1となる。その場合はハイフンを表示する
  return num > 0 ? `${num}位` : '-';
});

const ratioFormatter: ValueFormatter = {
  format: (value) => {
    if (value === VALUE_UNDEFINED) {
      return VALUE_UNDEFINED_JP;
    }

    const num = Number(value);
    check(!Number.isNaN(num));
    return `${Math.round(num * 10000) / 100}%`;
  },
  kana: () => ({}),
  supportsKana: () => false,
  asNumber: (value) => Math.round(Number(value) * 10000) / 100,
};

const rankRatioFormatter: ValueFormatter = {
  format: (value) => {
    const num = Number(value);
    check(defined(num));
    return `${Math.round(num * 100) / 100}位`;
  },
  kana: () => ({}),
  supportsKana: () => false,
  asNumber: (value) => Math.round(Number(value) * 100) / 100,
};

const translationFormatterCreator = (category: string): ValueFormatter =>
  createDefaultFormatter((value, i18n) => i18n.DATA.localize(value, category), {
    kana: (value, i18n) => {
      // フリガナが存在しない場合はその値を返す 値とフリガナが同じ場合があるため
      const kana = i18n.DATA_KANA.localize(value, category);
      return defined(kana) ? { kana } : {};
    },
    supportsKana: () => true,
  });

const ageFormatter: ValueFormatter = createDefaultFormatter((value) => {
  if (value === VALUE_UNDEFINED) {
    return VALUE_UNDEFINED_JP;
  }
  const num = Number(value);
  check(!Number.isNaN(num));
  const year = Math.trunc(num);
  const date = Math.round((num - year) * 1000);
  const yearString = year > 0 ? `${String(year)}歳と` : '';
  return `${yearString}${String(date)}日`;
});

class Diff {
  private lap: Optional<number>;
  private time: Optional<{
    hour: string;
    minute: string;
    second: string;
  }>;

  constructor(value: string) {
    let m = /^lap([0-9]{3})$/.exec(value);
    if (defined(m) && defined(m[1])) {
      this.lap = Number(m[1]);
    } else {
      m = /^([0-9]{2}):([0-9]{2}):([0-9]+.[0-9]+)$/.exec(value);
      if (defined(m) && defined(m[3])) {
        this.time = {
          hour: m[1] ?? '0',
          minute: m[2] ?? '0',
          second: m[3],
        };
      } else {
        check(false, `"${String(value)}"`);
      }
    }
  }

  getString(): string {
    if (defined(this.lap)) {
      return `${String(this.lap)}${this.lap > 1 ? 'laps' : 'lap'}`;
    } else if (defined(this.time)) {
      let timeString = this.time.second;
      const minute = parseInt(this.time.minute);
      if (minute !== 0) {
        timeString = `${String(minute)}:${timeString}`;
      }
      const hour = parseInt(this.time.hour);
      if (hour !== 0) {
        timeString = `${String(hour)}:${timeString}`;
      }
      return timeString;
    } else {
      return '';
    }
  }
}

const diffFormatter: ValueFormatter = createDefaultFormatter((value) =>
  new Diff(value).getString()
);

const yearFormatter: ValueFormatter = createDefaultFormatter((value, i18n) => {
  const raceName = formatRaceName(value, i18n) ?? '';
  return `${value}年 ${raceName}`;
});

export const formatRaceName = (
  yearString: string,
  i18n: I18n
): Optional<string> => {
  const year = parseInt(yearString);
  let raceName = undefined;
  for (const [threshold, label] of Object.entries(
    JSON.parse(i18n.LABELS.localize('race_name'))
  )) {
    if (year >= parseInt(threshold) && typeof label === 'string') {
      raceName = label;
    }
  }
  return raceName;
};

const countCircuitFormatter: ValueFormatter = createDefaultFormatter(
  (value) => `${value}個`
);

const raceIdFormatter: ValueFormatter = (() => {
  const format = (value: string, i18n: I18n, raceData: RaceData): string => {
    const race = raceData[value];
    if (defined(race)) {
      const formattedValue = `${race.year}年 Round${race.round}`;
      const index = race.index;
      if (defined(index)) {
        return formattedValue + ` Race${index + 1}`;
      } else {
        return formattedValue;
      }
    } else {
      return value;
    }
  };
  return createDefaultFormatter(format, {
    // pagination で開催順に表示するためraceId自体を使う
    kana: (value) => ({ kana: value.padStart(8, '0') }),
    supportsKana: () => true,
  });
})();

const carNumberFormatter: ValueFormatter = createDefaultFormatter(
  (value) => {
    check(Number.isInteger(Number(value)));
    return value;
  },
  {
    kana: (value) => ({ kana: value.padStart(8, '0') }),
    supportsKana: () => true,
    asNumber: () => NaN,
  }
);

// diff の値が数値で来るので asNumber はデフォルト実装を使用
const dateFormatter: ValueFormatter = createDefaultFormatter((value) => value);

const rawFormatter: ValueFormatter = createDefaultFormatter((value) => value, {
  asNumber: () => NaN,
});

const rawIntFormatter: ValueFormatter = createDefaultFormatter(
  (value) => {
    check(Number.isInteger(Number(value)));
    return value;
  },
  { asNumber: () => NaN }
);

const rawPointFormatter: ValueFormatter = createDefaultFormatter((value) => {
  check(Number.isInteger(Number(value) * 2));
  return Number(value).toString();
});

// gt300 -> GT300に変換する
const raceCategoryFormatter: ValueFormatter = createDefaultFormatter((value) =>
  value.toUpperCase()
);

const achievementTitleFormatter: ValueFormatter = createDefaultFormatter(
  (value, i18n) => {
    const m = /^(.+)_([0-9]+)$/.exec(value);
    if (defined(m?.[1]) && defined(m?.[2])) {
      // win_5 など
      const id = m[1];
      const number = m[2];
      return i18n.LABELS.localize(id, LABEL_CATEGORY.ACHIEVEMENT, {
        number,
      });
    } else {
      // champion など
      return i18n.LABELS.localize(value, LABEL_CATEGORY.ACHIEVEMENT);
    }
  }
);

const raceResultFormatter: ValueFormatter = createDefaultFormatter((value) => {
  // 決勝未開催の場合は数値部分が空文字列になる
  const regex = /^(-?\d*)(p)?(f)?$/;
  const match = regex.exec(value);
  if (!match) {
    throw new FormatError(value);
  }
  const numPart = match[1];
  let position = '';
  if (defined(numPart) && numPart.length > 0) {
    const num = parseInt(numPart, 10);
    // レース結果順位はリタイアの場合-1となる。その場合はハイフンを表示する
    position = num > 0 ? `${num}位` : '-';
  }
  const p = defined(match[2]) ? 'Ⓟ' : '';
  const f = defined(match[3]) ? 'Ⓕ' : '';

  return position + p + f;
});

const birthdayFormatter: ValueFormatter = createDefaultFormatter((value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }

  const [year, month, day] = value.split('-').map(Number);
  if (
    year === undefined ||
    month === undefined ||
    day === undefined ||
    isNaN(year) ||
    isNaN(month) ||
    isNaN(day) ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    return value;
  }

  const birthDate = new Date(year, month - 1, day);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }

  // 直前の誕生日からの日数計算
  const lastBirthday = new Date(today.getFullYear(), month - 1, day);
  if (lastBirthday > today) {
    // 今年の誕生日はまだなので、去年の誕生日を使用
    lastBirthday.setFullYear(today.getFullYear() - 1);
  }
  const daysFromLastBirthday = Math.floor(
    (today.getTime() - lastBirthday.getTime()) / (1000 * 60 * 60 * 24)
  );
  return `${value} (${age}年と${daysFromLastBirthday}日前)`;
});

export const historyTableYearFormatter: ValueFormatter = createDefaultFormatter(
  (value) => `${value}年`
);

export const historyTableRaceIdFormatter: ValueFormatter =
  createDefaultFormatter((value, i18n, raceData) => {
    const race = raceData[value];
    if (defined(race)) {
      return `Round${race.round}`;
    } else {
      return value;
    }
  });

// gt300 -> GT300に変換する
export const historyTableRaceCategoryFormatter: ValueFormatter =
  createDefaultFormatter((value) =>
    value !== '' ? `${value.toUpperCase()}クラス` : ''
  );

export const historyTableTitleFormatter = {
  format: (
    value: {
      title: string;
      raceKind: Optional<string>;
      number: Optional<string>;
      position: Optional<string>;
    },
    i18n: I18n
  ): string => {
    const ids = defined(value.number)
      ? [`${value.title}_${value.number}`, value.title]
      : [value.title];
    return i18n.LABELS.localize(ids, LABEL_CATEGORY.TITLE, {
      number: value.number,
      position: value.position,
    });
  },
};

createDefaultFormatter((value, i18n) => {
  const valueParts = value.split('-');
  const title = valueParts[0] ?? '';
  const race_kind_id = returnIf(valueParts[1], () => valueParts[1]);

  const race_kind_map: Record<string, string> = {
    race_kind_QF: '予選',
    race_kind_R: '決勝',
  };
  const race_kind = race_kind_map[race_kind_id ?? ''] ?? undefined;

  const m = /^(.+)_([0-9]+)$/.exec(title);
  let number;
  const titles = [title];
  if (defined(m?.[1]) && defined(m?.[2])) {
    // win_5 など
    titles.push(m[1]);
    number = m[2];
  }
  return i18n.LABELS.localize(titles, LABEL_CATEGORY.TITLE, {
    number,
    race_kind,
  });
});

const valueFormatterDict: { [name: string]: Optional<ValueFormatter> } = {
  // ランク
  rank: rankFormatter,
  // 平均順位
  position_avg: rankRatioFormatter,
  // レース順位
  position: rankFormatter,

  // 達成率
  avg: ratioFormatter,
  // 連続達成数
  cont: contTimesFormatter,
  // ランキングの項目
  champion: timesFormatter,
  entry: timesFormatter,
  fastest: timesFormatter,
  finished: timesFormatter,
  perfect: timesFormatter,
  podium: timesFormatter,
  pole: timesFormatter,
  pole_to_win: timesFormatter,
  win: timesFormatter,
  win_circuit: timesFormatter,
  count_circuit: countCircuitFormatter,

  fastest_age: ageFormatter,
  podium_age: ageFormatter,
  pole_age: ageFormatter,
  pole_to_win_age: ageFormatter,
  win_age: ageFormatter,

  win_diff: diffFormatter,

  finished_jump_up: rankFormatter,
  win_jump_up: rankFormatter,

  dominant_2: timesFormatter,
  dominant_3: timesFormatter,
  dominant_4: timesFormatter,
  dominant_5: timesFormatter,
  dominant_6: timesFormatter,

  best_race_result: raceResultFormatter,

  // 同時達成
  entry_double: timesFormatter,
  finished_double: timesFormatter,
  win_double: timesFormatter,
  podium_double: timesFormatter,
  dominant_2_double: timesFormatter,

  // 達成記録
  age: ageFormatter,
  race_date: dateFormatter,
  race_count: timesFormatter,

  // 翻訳対象
  ...mergeRecords(
    getLocalizeKeyNames().map((k) => ({
      [k]: translationFormatterCreator(k),
    }))
  ),

  race_year: yearFormatter,
  race_id: raceIdFormatter,
  race_category: raceCategoryFormatter,
  diff: diffFormatter,

  // 達成記録のタイトル。翻訳対象
  achievement: achievementTitleFormatter,

  // 翻訳非対象、そのまま表示
  car_number: carNumberFormatter,
  car_kind: rawFormatter,
  length: rawFormatter,
  race_name: rawFormatter,

  // 翻訳非対象、整数
  result_index: rawIntFormatter,

  // 翻訳非対象、小数
  driver_point: rawPointFormatter,
  total_driver_point: rawPointFormatter,
  total_driver_point_rank: rankFormatter,
  team_point: rawPointFormatter,
  total_team_point: rawPointFormatter,
  total_team_point_rank: rankFormatter,

  // 翻訳非対象、変換
  birthday: birthdayFormatter,
  round: rawPointFormatter,
};

export const getFormatterId = (name: string): string => {
  for (const [pattern, formatterId] of Object.entries(
    columnIdPatternsAndFormatterIds
  )) {
    if (new RegExp(pattern).test(name)) {
      return formatterId;
    }
  }
  for (const [columnId, formatterId] of Object.entries(
    columnIdsAndFormatterIds
  )) {
    if (columnId === name) {
      return formatterId;
    }
  }
  throw new Error(`Formatter ID is not registered: ${name}`);
};

const columnIdPatternsAndFormatterIds = {
  '^best_result\\?round=[0-9]+$': 'best_race_result',
  '^driver_point\\?round=[0-9]+$': 'driver_point',
};

export const columnIdsAndFormatterIds = {
  achieved_age: 'age',
  achieved_circuit: 'circuit',
  achieved_date: 'race_date',
  achieved_driver_name: 'driver_name',
  achieved_race_count: 'race_count',
  achieved_race_id: 'race_id',
  best_position_in_group_avg: 'avg',
  best_position_in_group_cont: 'cont',
  best_position_in_group_sum: 'win',
  champion_avg: 'avg',
  champion_cont: 'cont',
  champion_sum: 'champion',
  dominant_2_avg: 'avg',
  dominant_2_cont: 'cont',
  dominant_2_sum: 'dominant_2',
  dominant_3_avg: 'avg',
  dominant_3_cont: 'cont',
  dominant_3_sum: 'dominant_3',
  dominant_4_avg: 'avg',
  dominant_4_cont: 'cont',
  dominant_4_sum: 'dominant_4',
  dominant_5_avg: 'avg',
  dominant_5_cont: 'cont',
  dominant_5_sum: 'dominant_5',
  dominant_6_avg: 'avg',
  dominant_6_cont: 'cont',
  dominant_6_sum: 'dominant_6',
  double_dominant_2_avg: 'avg',
  double_dominant_2_cont: 'cont',
  double_dominant_2_sum: 'dominant_2_double',
  double_entry_cont: 'cont',
  double_entry_sum: 'entry_double',
  double_finished_avg: 'avg',
  double_finished_cont: 'cont',
  double_finished_sum: 'finished_double',
  double_podium_avg: 'avg',
  double_podium_cont: 'cont',
  double_podium_sum: 'podium_double',
  double_win_avg: 'avg',
  double_win_cont: 'cont',
  double_win_sum: 'win_double',
  entry_cont: 'cont',
  entry_sum: 'entry',
  entrant_pole: 'entrant', // SF用
  entrant_win: 'entrant', // SF用
  fastest_age_max: 'fastest_age',
  fastest_age_min: 'fastest_age',
  fastest_age_gap_max: 'fastest_age',
  fastest_age_gap_min: 'fastest_age',
  fastest_avg: 'avg',
  fastest_cont: 'cont',
  fastest_driver_name_QF_gt300: 'fastest_driver_name',
  fastest_driver_name_QF_gt500: 'fastest_driver_name',
  fastest_driver_name_R_gt300: 'fastest_driver_name',
  fastest_driver_name_R_gt500: 'fastest_driver_name',
  fastest_sum: 'fastest',
  finished_avg: 'avg',
  finished_cont: 'cont',
  finished_jump_up: 'finished_jump_up',
  finished_sum: 'finished',
  driver_name_pole: 'driver_name',
  driver_name_win: 'driver_name',
  machine_pole: 'machine',
  machine_win: 'machine',
  birthday: 'birthday',
  car_kind: 'car_kind',
  car_name: 'car_name',
  car_number: 'car_number',
  circuit: 'circuit',
  diff: 'diff',
  director: 'director',
  driver_name: 'driver_name',
  driver_pair_id: 'driver_name_in_pair',
  driver_team_id: 'driver_name_in_pair',
  driver_point: 'driver_point',
  entrant: 'entrant',
  fastest_driver_name: 'fastest_driver_name',
  length: 'length',
  machine: 'machine',
  manufacturer: 'manufacturer',
  podium_age_max: 'podium_age',
  podium_age_min: 'podium_age',
  podium_age_gap_max: 'podium_age',
  podium_age_gap_min: 'podium_age',
  podium_avg: 'avg',
  podium_circuit: 'count_circuit',
  podium_cont: 'cont',
  podium_first_year: 'podium',
  podium_sum: 'podium',
  pole_age_max: 'pole_age',
  pole_age_min: 'pole_age',
  pole_age_gap_max: 'pole_age',
  pole_age_gap_min: 'pole_age',
  pole_avg: 'avg',
  pole_by_races: 'pole',
  pole_cont: 'cont',
  pole_sum: 'pole',
  pole_to_win_age_max: 'pole_to_win_age',
  pole_to_win_age_min: 'pole_to_win_age',
  pole_to_win_age_gap_max: 'pole_to_win_age',
  pole_to_win_age_gap_min: 'pole_to_win_age',
  pole_to_win_avg: 'avg',
  pole_to_win_by_races: 'pole_to_win',
  pole_to_win_cont: 'cont',
  pole_to_win_sum: 'pole_to_win',
  position: 'position',
  position_avg: 'position_avg',
  race_id: 'race_id',
  race_name: 'race_name',
  race_year: 'race_year',
  round: 'round',
  total_driver_point: 'total_driver_point',
  total_driver_point_rank: 'total_driver_point_rank',
  total_driver_point_sum: 'total_driver_point',
  tyre: 'tyre',
  win_age_max: 'win_age',
  win_age_min: 'win_age',
  win_age_gap_max: 'win_age',
  win_age_gap_min: 'win_age',
  win_avg: 'avg',
  win_by_races: 'win',
  win_circuit: 'count_circuit',
  win_cont: 'cont',
  win_first_year: 'win',
  win_jump_up: 'win_jump_up',
  win_max_interval: 'win',
  win_sum: 'win',
};
